'use client';

import type { GiftOrderProductPhotoReviewDto } from '__generated__/api/unauth';
import SvgChevronleft from '@common/__generated__/icon/SvgChevronleft';
import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';

import { PHOTO_REVIEW_BENEFIT_AMOUNT } from '@/app/donation/_hooks/usePhotoReviewEvent/constants';
import { usePhotoReviewEvent } from '@/app/donation/_hooks/usePhotoReviewEvent/usePhotoReviewEvent';
import { BREAKPOINTS, WEBVIEW_TYPE_ENTRY } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';

import { WeeklyBestPhotoReviewListItem } from './WeeklyBestPhotoReviewListItem';

interface WeeklyBestPhotoReviewSectionProps {
  items: GiftOrderProductPhotoReviewDto[];
  className?: string;
}

export const WeeklyBestPhotoReviewSection = ({
  items,
  className,
}: WeeklyBestPhotoReviewSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
  });

  //* 슬라이드 변경 이벤트 리스너
  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setCurrentSlide(index);
    };

    emblaApi.on('select', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi]);
  const { isEventProcess } = usePhotoReviewEvent();
  const { entry } = useWebviewTypeEntry();

  const CHUNK_SIZE = isDesktop ? 6 : 2;

  //* 배열을 나누기
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }

    return chunks;
  };

  const chunkedList = chunkArray(items, CHUNK_SIZE);
  const isShowBenefit = isEventProcess && entry === WEBVIEW_TYPE_ENTRY.WELLO;
  const isNotRender = items.length < 1;

  if (isNotRender) return null;

  return (
    <section
      className={cn(
        'flex w-full justify-center bg-Y-25 pt-60',
        'mobile-tablet:pt-32',
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-col items-center gap-24 px-40 content-container',
          'mobile-tablet:gap-16 mobile-tablet:px-0',
        )}
      >
        <header
          className={cn(
            'grid w-full grid-cols-[1fr_auto_1fr] items-end',
            'mobile-tablet:flex mobile-tablet:items-center mobile-tablet:px-20',
          )}
        >
          <div className="mobile-tablet:hidden" />
          <header className="flex items-center gap-8">
            <h2
              className={cn(
                'font-800 text-Gray-900 typo-h4',
                'mobile-tablet:font-700 mobile-tablet:typo-body1',
              )}
            >
              주간 BEST 포토리뷰!
            </h2>
            {isShowBenefit && (
              <p className="whitespace-nowrap rounded-xsmall bg-O-400 px-6 pb-2 pt-3 font-600 text-Gray-white typo-caption1">
                포토후기 {formatAmount()} 지급
              </p>
            )}
          </header>
          <div
            className={cn(
              'flex flex-1 items-center justify-end gap-8',
              'mobile-tablet:hidden',
            )}
          >
            <p className="font-500 text-Gray-550 typo-body4">
              <span className="font-700 text-Gray-600">{currentSlide + 1}</span>{' '}
              / {chunkedList.length}
            </p>
            <div className="flex">
              <Button
                theme="sub-line"
                className="size-24 rounded-[2px] bg-Gray-white p-0"
                onClick={() => emblaApi?.scrollPrev()}
              >
                <SvgChevronleft className="size-18 text-Gray-500" />
              </Button>
              <Button
                theme="sub-line"
                className="size-24 rounded-[2px] bg-Gray-white p-0"
                onClick={() => emblaApi?.scrollNext()}
              >
                <SvgChevronright className="size-18 text-Gray-500" />
              </Button>
            </div>
          </div>
        </header>

        <div
          className={cn(
            'w-full overflow-hidden pb-60',
            'mobile-tablet:px-20 mobile-tablet:pb-32',
          )}
          ref={emblaRef}
        >
          <div className="flex">
            {chunkedList.map((chunk, chunkIndex) => (
              <div
                key={chunkIndex}
                className={cn(
                  isDesktop
                    ? 'min-w-0 flex-[0_0_100%]'
                    : 'mr-32 min-w-0 flex-[0_0_auto] last:mr-0',
                )}
              >
                <ul
                  className={cn(
                    'flex flex-wrap gap-16',
                    'mobile-tablet:flex-col mobile-tablet:gap-12',
                  )}
                >
                  {chunk.map(
                    (
                      {
                        reviewId: review_id,
                        contents,
                        productName: product_name,
                        rating,
                        fileUrl: file_url,
                      },
                      index,
                    ) => (
                      <WeeklyBestPhotoReviewListItem
                        key={`${chunkIndex}-${index}`}
                        reviewId={review_id}
                        productThumbnailUrl={file_url}
                        productName={product_name}
                        rating={rating}
                        reviewContent={contents}
                      />
                    ),
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const formatAmount = () => {
  if (PHOTO_REVIEW_BENEFIT_AMOUNT >= 10000) {
    return `${PHOTO_REVIEW_BENEFIT_AMOUNT / 10000}만원`;
  }
  if (PHOTO_REVIEW_BENEFIT_AMOUNT >= 1000) {
    return `${PHOTO_REVIEW_BENEFIT_AMOUNT / 1000}천원`;
  }

  return `${PHOTO_REVIEW_BENEFIT_AMOUNT}원`;
};
